import { useEffect, useRef, useState } from "react";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import { fetchWithAuth, isAuthNotReadyError } from "@/lib/utils";

export interface Me {
  email: string;
  name?: string;
  user_id?: string;
  tenant: string;
  /** Per-tenant model-switcher capability; absent on older bridges. */
  model_switcher_enabled?: boolean;
  /** WorkOS RBAC role (e.g. "admin", "member"). */
  role?: string;
  /** Server-computed platform-admin status from verified WorkOS claims. */
  is_platform_admin?: boolean;
  /** Server-computed tenant-admin status from verified WorkOS claims. */
  is_tenant_admin?: boolean;
  /** Verified JWT tenant available to a tenant-admin, never a header override. */
  tenant_admin_tenant?: string | null;
  /** WorkOS Dashboard actor when the session is impersonated. */
  impersonator?: {
    email: string;
    reason: string | null;
  } | null;
}

export interface UseMeReturn {
  me: Me | null;
  /** True until the first /api/me response (success or failure) resolves. */
  loading: boolean;
}

export interface TenantAgentEditorAccess {
  /** Existing ae10 platform-admin capability. */
  isPlatformAdmin: boolean;
  /** Tenant locked to the tenant-admin's verified JWT, if applicable. */
  tenantId: string | null;
  /** Whether the UI may expose the shared-agent editor entry point. */
  canManageTenantAgents: boolean;
}

/**
 * Derive the narrowly scoped agent-editor capability from server-resolved
 * identity data. Older Chat Bridge deployments omit the new status fields,
 * so retain a raw-role fallback solely for UI compatibility; the server is
 * always the authority for the actual agent-config request.
 */
export function getTenantAgentEditorAccess(me: Me | null): TenantAgentEditorAccess {
  const isPlatformAdmin = me?.is_platform_admin
    ?? (me?.tenant === "ae10" && me?.role === "admin");
  const isTenantAdmin = me?.is_tenant_admin ?? (me?.role === "org-tenant-admin");
  const isLegacyIdentityEnvelope = me?.is_tenant_admin === undefined;
  const tenantId = isTenantAdmin
    ? (me?.tenant_admin_tenant ?? (isLegacyIdentityEnvelope ? me?.tenant ?? null : null))
    : null;

  return {
    isPlatformAdmin,
    tenantId,
    canManageTenantAgents: isPlatformAdmin || Boolean(tenantId),
  };
}

const ME_URL = "/api/me";
const MAX_WAIT_MS = 15_000;

/**
 * Fetches the current user's identity — including the backend-resolved
 * tenant — once on mount. The tenant is used for UI gating only.
 *
 * This hook is resilient to AuthKit's session still loading on mount: it
 * waits for the access token to become available before calling /api/me,
 * and backs off quietly while the auth state initializes.
 */
export function useMe(getToken: () => Promise<string>, identityKey?: string | null): UseMeReturn {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const backendReconnectKey = useBackendReconnectKey();
  const previousIdentityKeyRef = useRef(identityKey);

  useEffect(() => {
    const identityChanged = previousIdentityKeyRef.current !== identityKey;
    previousIdentityKeyRef.current = identityKey;
    // Revalidating the same authenticated user must not briefly revoke the
    // verified cache scope: that would clear the mounted message feed and
    // sidebar before the reconnect request has even returned.
    if (identityChanged) {
      setMe(null);
      setLoading(true);
    }
    const retainCurrentIdentity = !identityChanged && me !== null;
    const abort = new AbortController();
    let cancelled = false;
    let finished = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let watchdog: ReturnType<typeof setTimeout>;

    function finish(value: Me | null, authorizationFailed = false) {
      if (cancelled || finished) return;
      finished = true;
      clearTimeout(watchdog);
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      if (value || !retainCurrentIdentity || authorizationFailed) setMe(value);
      setLoading(false);
    }

    async function attempt() {
      if (cancelled || finished) return;

      try {
        const res = await fetchWithAuth(
          ME_URL,
          { headers: {}, signal: abort.signal },
          () => getTokenRef.current()
        );
        if (res.status === 401 || res.status === 403) {
          finish(null, true);
          return;
        }
        if (!res.ok) throw new Error(`/api/me returned ${res.status}`);
        const data = (await res.json()) as Me;
        finish(data);
      } catch (err) {
        if (cancelled || finished) return;

        if (isAuthNotReadyError(err)) {
          // Auth session still loading — retry until the token is ready or
          // the component unmounts. We don't log here to avoid console noise
          // during the normal initial load window.
          retryTimer = setTimeout(() => void attempt(), 500);
          return;
        }

        console.warn("[ui] useMe failed:", err);
        finish(null);
      }
    }

    watchdog = setTimeout(() => {
      abort.abort();
      if (!cancelled && !finished) {
        console.warn("[ui] useMe timed out waiting for auth");
        finish(null);
      }
    }, MAX_WAIT_MS);

    void attempt();

    return () => {
      cancelled = true;
      clearTimeout(watchdog);
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      abort.abort();
    };
  }, [backendReconnectKey, identityKey]);

  return { me, loading };
}
